/* MolliePaymentReturnPage — iter913
 *
 * Landing page after Mollie hosted checkout returns the shopper. Reads
 * `tx_id` from the query string (fallback: sessionStorage), polls
 * `GET /api/payments/mollie/status/{tx_id}` every 3 s, and shows one of
 * four states:
 *
 *   • processing  — Mollie hasn't confirmed yet (open / pending / authorized)
 *   • paid        — success, credits/plan applied
 *   • failed      — canceled / expired / failed
 *   • error       — network / auth error, keeps retrying
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40; // ≈ 2 min

const FINAL_FAILURES = new Set(["canceled", "expired", "failed"]);

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [txId] = useState(
    () =>
      searchParams.get("tx_id") ||
      sessionStorage.getItem("mollie_last_tx_id") ||
      ""
  );
  const [status, setStatus] = useState("polling");
  const [payment, setPayment] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const t = useCallback((k) => {
    const dict = {
      processing_title:  bg ? "Плащането се обработва" : "Payment processing",
      processing_body:   bg
        ? "Изчакваме потвърждение от Mollie. Обикновено отнема 5–30 секунди за карти и iDEAL, до 2 работни дни за банков превод."
        : "Waiting for confirmation from Mollie. Usually 5–30 seconds for card & iDEAL, up to 2 business days for bank transfer.",
      paid_title:        bg ? "Плащането е потвърдено!" : "Payment confirmed!",
      paid_body:         bg
        ? "Планът ви е активиран. Ще получите потвърдителен email в следващите минути."
        : "Your plan has been activated. You'll receive a confirmation email shortly.",
      failed_title:      bg ? "Плащането не беше завършено" : "Payment did not complete",
      failed_body:       bg
        ? "Не се притеснявайте — не сме таксували картата ви. Можете да опитате отново."
        : "Don't worry — we did not charge your card. You can try again.",
      to_dashboard:      bg ? "Към таблото" : "Go to Dashboard",
      try_again:         bg ? "Опитай отново" : "Try again",
      missing_tx:        bg
        ? "Липсва референция на плащане. Върнете се към страницата с планове."
        : "Missing payment reference. Please return to the pricing page.",
    };
    return dict[k] || k;
  }, [bg]);

  useEffect(() => {
    if (!txId) {
      setStatus("missing");
      return;
    }
    let cancelled = false;
    let timer = null;

    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("nutramilo_access_token") ||
      "";
    const auth = { Authorization: `Bearer ${token}` };

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${API}/payments/mollie/status/${encodeURIComponent(txId)}`,
          { headers: auth }
        );
        if (cancelled) return;
        setPayment(data);
        setAttempts((n) => n + 1);
        if (data?.status === "paid" || data?.mollie_status === "paid") {
          setStatus("paid");
          // Clear the sessionStorage token now that we're done.
          try { sessionStorage.removeItem("mollie_last_tx_id"); } catch { /* ignore */ }
          return;
        }
        if (FINAL_FAILURES.has(data?.mollie_status)) {
          setStatus("failed");
          return;
        }
        // Still processing — schedule next tick.
        if (attempts < MAX_POLL_ATTEMPTS) {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("timeout");
        }
      } catch (e) {
        if (cancelled) return;
        // Auth failure or 404 → treat as terminal.
        const code = e?.response?.status;
        if (code === 401 || code === 403 || code === 404) {
          setStatus("failed");
        } else if (attempts < MAX_POLL_ATTEMPTS) {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("timeout");
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId]);

  const Icon =
    status === "paid"
      ? CheckCircle2
      : status === "failed"
        ? XCircle
        : status === "missing" || status === "timeout"
          ? AlertTriangle
          : Loader2;

  const iconClass =
    status === "paid"
      ? "text-emerald-500"
      : status === "failed"
        ? "text-red-500"
        : status === "missing" || status === "timeout"
          ? "text-amber-500"
          : "text-[#0EA5E9] animate-spin";

  const title =
    status === "paid"
      ? t("paid_title")
      : status === "failed"
        ? t("failed_title")
        : status === "missing"
          ? t("missing_tx")
          : t("processing_title");

  const body =
    status === "paid"
      ? t("paid_body")
      : status === "failed"
        ? t("failed_body")
        : status === "missing"
          ? ""
          : t("processing_body");

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        isDark
          ? "bg-background"
          : "bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4]"
      }`}
      data-testid="mollie-return-page"
    >
      <Card
        className={`w-full max-w-md ${
          isDark ? "bg-card border-border" : "border-slate-100 shadow-lg"
        }`}
      >
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center mx-auto mb-5">
            <Icon className={`w-10 h-10 ${iconClass}`} />
          </div>
          <h2
            className={`text-xl font-bold mb-2 ${
              isDark ? "text-white" : "text-[#1A1A2E]"
            }`}
            data-testid="mollie-return-title"
          >
            {title}
          </h2>
          {body && (
            <p
              className={`text-sm mb-6 ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}
              data-testid="mollie-return-body"
            >
              {body}
            </p>
          )}
          {txId && (
            <p className="text-[10px] font-mono text-gray-400 mb-4">
              tx: {txId}
              {payment?.amount && (
                <>
                  {" · "}
                  {payment.amount.toFixed(2)} {payment.currency || "EUR"}
                </>
              )}
            </p>
          )}
          {status === "paid" ? (
            <Button
              onClick={() => navigate("/dashboard")}
              data-testid="mollie-return-to-dashboard"
              className="w-full bg-gradient-to-r from-[#7B1FA2] to-[#F06292] text-white h-11 rounded-xl font-semibold"
            >
              {t("to_dashboard")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : status === "failed" ? (
            <div className="space-y-2">
              <Button
                onClick={() => navigate("/pricing")}
                data-testid="mollie-return-try-again"
                className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white h-11 rounded-xl font-semibold"
              >
                {t("try_again")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="w-full"
                data-testid="mollie-return-dashboard-alt"
              >
                {t("to_dashboard")}
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              data-testid="mollie-return-skip"
              className="w-full h-11 rounded-xl"
            >
              {t("to_dashboard")}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
